import React, { useRef } from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import Image from 'next/image';
import TopicsListDropdown from '../header/TopicsListDropdown';
import useModal from '../../hooks/useModal';
import useClickOutside from '../../hooks/useClickOutside';
import RotatingIcon from '../RotatingIcon';
import { LabelComponent } from './LabelComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import LibraryShareButton from './BasicShareButton';
import { INFO_SIGN } from '@osmosis/constants';
import { replaceS3ToCloudfront } from '../../lib/util/url';
import { bgPrimaryText } from '@osmosis/tailwind-ui/styles/background';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import OsmosisAIButton from '../ai/OsmosisAIButton';

type Props = {
  headerData: {
    icon: string;
    levelOneTitle: string;
    selected: string;
    shareData: {
      include: string[];
      withText: boolean;
    };
  };
  l2HeaderLabel: string;
};

/**
 * LibraryHeader.
 */
const LibraryHeader = (props: Props): JSX.Element => {
  const { icon, levelOneTitle, selected } = props.headerData;
  const { l2HeaderLabel } = props;
  const topicsDropdown = useModal();
  const { getClassValue } = useContentSize();
  const intl = useIntl();

  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  useClickOutside([ref, buttonRef], () => {
    if (topicsDropdown.isVisible) {
      topicsDropdown.hide();
    }
  });

  const iconUrl = replaceS3ToCloudfront(icon);

  const containerResponsiveClasses = getClassValue({
    defaultClasses: 'md:h-24 md:py-6 md:pl-4 md:pr-3 lg:h-16',
    breakpoints: {
      md: 'h-24 py-6 pl-4 pr-3',
      lg: 'h-16 py-6 pl-4 pr-3',
    },
  });

  const shareButtonResponsiveClasses = getClassValue({
    defaultClasses: 'md:block',
    breakpoints: {
      md: 'block',
    },
  });
  return (
    <Flex
      className={cn(
        bgPrimaryText,
        `w-full z-10 items-center justify-between gap-2 overflow-hidden py-3 pl-2 pr-2`,
        containerResponsiveClasses,
      )}
      data-testid="library-header-container"
    >
      <Flex className="flex items-center">
        {!!iconUrl && (
          <Box className="mr-3 min-h-[50px] min-w-[50px]">
            <Image
              src={iconUrl}
              alt={`${levelOneTitle}-image`}
              width={50}
              height={50}
            />
          </Box>
        )}
        <Flex className="flex flex-col justify-around">
          <Box>
            <H1
              className={`${text0} tracking-custom inline !text-xs font-bold`}
              data-testid="library-header-level-one-title"
            >
              {levelOneTitle.split(INFO_SIGN)[0]}
            </H1>
            {levelOneTitle.includes(INFO_SIGN) && (
              <P
                className="text-invert tracking-custom inline text-xs font-bold hover:cursor-pointer"
                data-testid="library-header-level-one-title-info-icon"
              >
                {INFO_SIGN}
              </P>
            )}
          </Box>

          <Button
            className={`justify-start px-0 py-0 ${bgPrimaryText} active:bg-blue-700`}
            data-testid="library-dropdown-toggle-icon"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              topicsDropdown.toggle();
            }}
            ref={buttonRef}
            aria-expanded={topicsDropdown.isVisible}
            aria-label={intl.formatMessage({
              id: 'library.header.toggle.video.topics',
              defaultMessage: 'Toggle video topics',
            })}
            aria-controls="header-topic-dropdown"
          >
            <span
              className="text-invert mr-3 text-left !text-xl"
              data-testid="library-header-selected-title"
            >
              {selected}
            </span>
            {l2HeaderLabel === 'new' && (
              <LabelComponent
                content={
                  <FormattedMessage
                    defaultMessage="NEW"
                    id="library.header.l2.tag.new"
                  />
                }
                dataTestId={`new-tag-${selected}`}
                containerProps={{ style: { minWidth: '65px' } }}
              />
            )}

            <Box className="text-invert text-xl">
              <RotatingIcon isRotated={topicsDropdown.isVisible} />
            </Box>
          </Button>

          <TopicsListDropdown
            isVisible={topicsDropdown.isVisible}
            hide={topicsDropdown.hide}
            dataTestid="library-topic-dropdown"
            ref={ref}
            motionContainerStyle={{ width: '90%', top: 'calc(100% + 30px)' }}
          />
        </Flex>
      </Flex>
      <Flex>
        <Box className={cn(`mr-2 hidden`, shareButtonResponsiveClasses)}>
          <LibraryShareButton />
        </Box>

        <OsmosisAIButton
          source="Library"
          classNames={{
            button: 'border-none',
          }}
        />
      </Flex>
    </Flex>
  );
};

export default LibraryHeader;
